'use client';

import { Image } from '@/components/ui/image/image';
import { Link } from '@/components/ui/link';
import { Media } from '@/components/ui/media/media';
import RichTextViewer from '@/components/ui/rich-text-viewer/rich-text-viewer';
import { cn } from '@/lib/utils';
import { buildFormContentFromLinkFields } from '@/lib/utils/form-data';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import type { SlideItem } from '../../hero-slider';
import styles from './slider-carousel.module.scss';

export type SliderCarouselProps = {
  slides: SlideItem[];
  activeIndex: number;
  onSlideChange: (index: number) => void;
  className?: string;
};

export const SliderCarousel = ({
  slides,
  activeIndex,
  onSlideChange,
  className,
}: SliderCarouselProps) => {
  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: activeIndex,
    slideChanged(slider) {
      onSlideChange(slider.track.details.rel);
    },
    slides: {
      perView: 1.15,
      spacing: 16,
    },
  });

  const handleDotClick = (index: number) => {
    instanceRef.current?.moveToIdx(index);
  };

  return (
    <div className={cn(styles.carousel, className)}>
      {/* Pagination Dots */}
      <div className={styles.dots}>
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => handleDotClick(index)}
            className={cn(styles.dot, index === activeIndex && styles.dot_active)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Slides */}
      <div ref={sliderRef} className="keen-slider">
        {slides.map((slide, index) => {
          const ctaFields = slide.cta?.fields;
          const formContent = ctaFields ? buildFormContentFromLinkFields(ctaFields) : {};

          return (
            <div key={`${slide.id}-${index}`} className="keen-slider__slide">
              <div className={styles.card}>
                <div className={styles.card_content}>
                  <h2 className={styles.card_title}>{slide.title}</h2>
                  {slide.description && (
                    <div className={styles.card_description}>
                      <RichTextViewer content={slide.description} />
                    </div>
                  )}
                  {ctaFields && (
                    <div className={styles.card_cta}>
                      <Link
                        label={ctaFields.label}
                        url={ctaFields.url}
                        type={ctaFields.type}
                        withArrow
                        destination={ctaFields.destination}
                        ctaId={ctaFields.key}
                        {...formContent}
                      />
                    </div>
                  )}
                </div>
                {(slide.mediaData || slide.illustration?.src) && (
                  <div className={styles.card_illustration}>
                    {slide.mediaData ? (
                      <Media
                        {...slide.mediaData}
                        width={250}
                        height={200}
                        className={styles.card_illustration_image}
                      />
                    ) : slide.illustration?.src ? (
                      <Image
                        src={slide.illustration.src}
                        alt={slide.illustration.alt || ''}
                        width={250}
                        height={200}
                        className={styles.card_illustration_image}
                      />
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SliderCarousel;
